// components/Hero.js
import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import './Hero.css'
import API_BASE_URL from '../../config'

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [banners, setBanners] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isAutoPlaying] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const navigate = useNavigate();

  const fetchBanners = useCallback(async () => {
    try {
      setIsLoading(true);
      setHasError(false);
      const response = await fetch(`${API_BASE_URL}/api/banners/active`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch banners');
      }
      
      const data = await response.json();
      setBanners(data);
    } catch (error) {
      console.error('Error fetching banners:', error);
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  // Auto slide functionality
  useEffect(() => {
    if (banners.length <= 1 || !isAutoPlaying || isTransitioning) return;

    const interval = setInterval(() => {
      handleNextSlide();
    }, 4000);

    return () => clearInterval(interval);
  });

  const handleNextSlide = useCallback(() => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    setCurrentSlide((prev) => (prev + 1) % banners.length);
    
    // Reset transitioning state after animation completes
    setTimeout(() => {
      setIsTransitioning(false);
    }, 300);
  }, [banners.length, isTransitioning]);

  const handlePrevSlide = useCallback(() => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    setCurrentSlide((prev) => (prev - 1 + banners.length) % banners.length);
    
    // Reset transitioning state after animation completes
    setTimeout(() => {
      setIsTransitioning(false);
    }, 300);
  }, [banners.length, isTransitioning]);

  const goToSlide = useCallback((index) => {
    if (isTransitioning || index === currentSlide) return;
    
    setIsTransitioning(true);
    setCurrentSlide(index);
    
    // Reset transitioning state after animation completes
    setTimeout(() => {
      setIsTransitioning(false);
    }, 300);
  }, [currentSlide, isTransitioning]);

  // Handle image click with category redirection
  const handleImageClick = useCallback((banner) => {
    if (banner.redirectUrl) {
      navigate(banner.redirectUrl);
    } else {
      // Fallback to category-based routing
      switch (banner.category) {
        case 'mens':
          navigate('/mens');
          break;
        case 'womens':
          navigate('/womens');
          break;
        case 'kids':
          navigate('/kids');
          break;
        case 'all':
          navigate('/products');
          break;
        default:
          navigate('/products');
      }
    }
  }, [navigate]);

  // Get slide position class
  const getSlidePosition = (index) => {
    if (index === currentSlide) return 'active';
    if (index === (currentSlide - 1 + banners.length) % banners.length) return 'prev';
    if (index === (currentSlide + 1) % banners.length) return 'next';
    return '';
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="hero">
        <div className="hero-loading">
          <div className="loading-spinner"></div>
          <p>Loading amazing offers...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (hasError) {
    return (
      <div className="hero">
        <div className="hero-error">
          <div className="error-icon">⚠️</div>
          <p>Unable to load banners</p>
          <button className="retry-btn" onClick={fetchBanners}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Show empty state only if there are truly no banners
  if (banners.length === 0) {
    return (
      <div className="hero">
        <div className="hero-placeholder">
          <div className="placeholder-icon">🎯</div>
          <h3>Special Offers Coming Soon!</h3>
          <p>Stay tuned for amazing deals and promotions</p>
        </div>
      </div>
    );
  }

  return (
    <div className="hero">
      <div className="hero-slider">
        <div className="hero-slides-container">
          {banners.map((banner, index) => (
            <div
              key={banner._id}
              className={`hero-slide ${getSlidePosition(index)}`}
              style={{
                backgroundImage: `url(${API_BASE_URL}${banner.image})`
              }}
              onClick={() => handleImageClick(banner)}
              role="button"
              tabIndex={0}
              aria-label={`View ${banner.title || 'banner'} offers`}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleImageClick(banner);
                }
              }}
            >
              {/* Removed slide-content since it's hidden in CSS */}
            </div>
          ))}
        </div>
        
        {/* Navigation Arrows */}
        {banners.length > 1 && (
          <>
            <button 
              className="slider-arrow prev-arrow" 
              onClick={handlePrevSlide}
              aria-label="Previous slide"
              disabled={isTransitioning}
            >
              ‹
            </button>
            <button 
              className="slider-arrow next-arrow" 
              onClick={handleNextSlide}
              aria-label="Next slide"
              disabled={isTransitioning}
            >
              ›
            </button>

            {/* Dots Indicator */}
            <div className="slider-dots">
              {banners.map((_, index) => (
                <button
                  key={index}
                  className={`dot ${index === currentSlide ? 'active' : ''}`}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                  disabled={isTransitioning}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default Hero

